import { useRef, useState } from "react";
import { api, imgSrc } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X } from "lucide-react";

// Generic text field
export function TextField({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="block tracking-eyebrow text-[#4B5563] mb-1.5">{label}</span>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E5E7EB] focus:border-[#D4AF37] outline-none py-2.5 px-3 text-sm transition-colors" />
    </label>
  );
}

export function TextArea({ label, value, onChange, rows = 4 }) {
  return (
    <label className="block">
      <span className="block tracking-eyebrow text-[#4B5563] mb-1.5">{label}</span>
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[#E5E7EB] focus:border-[#D4AF37] outline-none py-2.5 px-3 text-sm resize-y transition-colors" />
    </label>
  );
}

export function ImageField({ label, value, onChange, token }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!token) { toast.error("Enter admin token first."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Image uploaded.");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <span className="block tracking-eyebrow text-[#4B5563] mb-1.5">{label}</span>
      <div className="flex items-start gap-3">
        <div className="w-24 h-24 bg-[#F3F4F6] border border-[#E5E7EB] overflow-hidden flex-shrink-0 flex items-center justify-center">
          {value ? <img src={imgSrc(value)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#9CA3AF]" />}
        </div>
        <div className="flex-1 space-y-2">
          <input type="text" value={value ?? ""} onChange={(e) => onChange(e.target.value)}
            placeholder="Image URL or upload below"
            className="w-full bg-white border border-[#E5E7EB] focus:border-[#D4AF37] outline-none py-2 px-3 text-xs" />
          <div className="flex gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white transition-colors inline-flex items-center gap-1.5 disabled:opacity-60">
              <Upload className="w-3 h-3" /> {uploading ? "Uploading..." : "Upload"}
            </button>
            {value && <button type="button" onClick={() => onChange("")} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 border border-[#E5E7EB] hover:border-[#1A1A1A] inline-flex items-center gap-1.5"><X className="w-3 h-3" /> Clear</button>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </div>
      </div>
    </div>
  );
}

// List editor: renders each item with a render fn, supports add/remove/reorder
export function ListEditor({ label, items, onChange, renderItem, newItem }) {
  const update = (i, val) => { const copy = [...items]; copy[i] = val; onChange(copy); };
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...(items || []), typeof newItem === "function" ? newItem() : newItem]);
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const copy = [...items];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="tracking-eyebrow text-[#4B5563]">{label} ({items?.length || 0})</span>
        <button type="button" onClick={add} className="text-xs uppercase tracking-widest font-semibold px-3 py-1.5 bg-[#D4AF37] text-white hover:bg-[#B89047]">+ Add</button>
      </div>
      <div className="space-y-3">
        {(items || []).map((it, i) => (
          <div key={i} className="border border-[#E5E7EB] bg-[#FAFAFA] p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-[#4B5563]">#{i + 1}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => move(i, -1)} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#E5E7EB] hover:border-[#1A1A1A]">↑</button>
                <button type="button" onClick={() => move(i, 1)} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#E5E7EB] hover:border-[#1A1A1A]">↓</button>
                <button type="button" onClick={() => remove(i)} className="text-[10px] uppercase tracking-widest px-2 py-1 border border-red-300 text-red-600 hover:bg-red-50">Remove</button>
              </div>
            </div>
            {renderItem(it, (val) => update(i, val), i)}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Section({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E5E7EB] bg-white">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#FAFAFA]">
        <span className="font-display text-lg">{title}</span>
        <span className="tracking-eyebrow text-[#D4AF37]">{open ? "Close" : "Edit"}</span>
      </button>
      {open && <div className="px-5 pb-6 pt-2 border-t border-[#E5E7EB] space-y-4">{children}</div>}
    </div>
  );
}
